import { fromZodSchema, useForm } from 'formoid';
import { z } from 'zod';
import { Button, CheckboxField, Link, ModalContentProps, ModalHeader } from '~/common/components';
import { consentsMap } from '~/common/kits/consent/constants';
import { joinWithPunctuation } from '~/common/kits/consent/utils';
import { RequiredConsentName } from '~/root/Auth';

type Props = ModalContentProps & {
  onSubmit: (news_letter?: boolean) => void;
};

const requiredConsents: RequiredConsentName[] = ['terms_and_conditions', 'privacy_policy', 'dpa'];

export const SignupConsentModal = ({ onClose, onSubmit }: Props) => {
  const { fieldProps, handleSubmit } = useForm({
    initialValues: {
      consents: false,
      news_letter: false,
    },
    validationStrategy: 'onChange',
    validators: () =>
      fromZodSchema({
        consents: z.boolean().refine((val) => val, 'You must accept our Terms of Use'),
        news_letter: z.boolean().optional(),
      }),
  });

  const submit = () =>
    handleSubmit(async ({ news_letter }) => {
      onSubmit(news_letter);
      onClose();
    });

  return (
    <div className="max-w-[620px]">
      <ModalHeader
        onClose={onClose}
        title="Review & accept our Terms of Use"
        subtitle="Before you proceed, please review and accept the documents below."
      />
      <div className=" px-3 pb-3 space-y-3">
        <CheckboxField
          {...fieldProps('consents')}
          title={
            <>
              I agree to the{' '}
              {joinWithPunctuation(
                requiredConsents.map((consent) => (
                  <Link key={consent} href={consentsMap[consent].link} newTab noTruncate>
                    {consentsMap[consent].name}
                  </Link>
                )),
              )}
            </>
          }
        />
        <CheckboxField
          {...fieldProps('news_letter')}
          title="I'd like to receive occasional emails with information on new services and offers"
        />
        <Button color="secondary-navy" size="m" onClick={submit} className="w-full !mt-5">
          Continue
        </Button>
      </div>
    </div>
  );
};
